/**
 * HOP CHE CHU CU - phai bao het PHAM VI MUC, khong phai bao het HOP CHU.
 *
 * =============================================================================
 *  LOI DA XAY RA
 * =============================================================================
 *  Truoc day hop che chi la bbox cua khoi cong 0.5pt moi phia. Ket qua tren tai
 *  lieu tieng Viet: chu moi ve dung, nhung DAU THANH cua chu cu van noi lo lung
 *  phia tren - hop che co canh tren qua thap.
 *
 *  Nguyen nhan: bbox lay tu pdf.js xap xi CO CHU (khoang 1.0em), trong khi tieng
 *  Viet chong hai tang dau tren mot nguyen am - dau am (mu, trang, moc) cong dau
 *  thanh (huyen, sac, hoi, nga, nang). Do tren chinh font dong goi Noto Sans:
 *
 *      chu thuong ASCII            dinh muc 0.546em
 *      chu HOA ASCII               dinh muc 0.717em
 *      chu HOA + dau chong 2 tang  dinh muc 1.048em   <-- gan gap doi
 *      day (chu co duoi + dau)          -0.240em
 *
 *  1.048em vuot ra ngoai hop cao 1.0em. Do la phan dau bi thua ra.
 *
 * =============================================================================
 *  VI SAO KHONG DEM MOT CON SO CO DINH CHO XONG
 * =============================================================================
 *  Vi che qua tay cung la loi, va la loi TE HON. Trong bang day, buoc dong co
 *  the chi khoang 1.2em. Mot hop che cao 1.29em se an sang HANG BEN CANH va xoa
 *  trang mot phan chu khong lien quan - nguoi dung mat du lieu ma khong he biet.
 *
 *  Vi the: DO pham vi muc that cua CHINH CHUOI CHU CU, roi hop nhat voi bbox da
 *  bao cao. Chuoi khong dau thi khong no them ti nao. Chuoi co dau chong tang
 *  thi no dung phan can thiet.
 */
import type { PdfRect } from '@pdfeditor/types';
import type { Font } from '../fonts/fontkit';

/** Pham vi muc quanh duong co so, don vi em. top duong len, bottom am xuong. */
export interface InkBand {
  topEm: number;
  bottomEm: number;
}

/**
 * Duong lui khi khong do duoc font. Lay tu so do that tren Noto Sans, la truong
 * hop XAU NHAT cua tieng Viet (chu HOA co dau chong hai tang + chu co duoi).
 *
 * Dung khi va chi khi khong do duoc: doan rong con hon de lo dau chu cu, nhung
 * chi trong tinh huong ta that su khong biet.
 */
export const FALLBACK_INK_BAND: InkBand = Object.freeze({ topEm: 1.05, bottomEm: -0.24 });

/** Khong bao gio che thap hon muc nay, ke ca khi chuoi cu rong. */
const MIN_INK_BAND: InkBand = Object.freeze({ topEm: 0.75, bottomEm: -0.21 });

/**
 * Tran no them tinh tu canh bbox, don vi em.
 *
 * Chan tren nay la de phong: neu bbox bao cao sai lech nhieu, ta van khong an
 * sang hang ben canh. 0.45em du cho khoang cach giua dinh muc dau chong tang
 * (1.048em) va dinh hop chu dien hinh (~0.75em), va van nho hon buoc dong chat
 * nhat gap trong thuc te.
 */
const MAX_GROWTH_EM = 0.45;

/**
 * Do pham vi muc that cua mot chuoi bang chuong trinh font.
 * Tra ve null khi font khong cho do duoc - ben goi phai dung FALLBACK_INK_BAND.
 */
export function measureInkBand(font: Font | null, text: string): InkBand | null {
  if (!font || !text) return null;
  const upm = (font as unknown as { unitsPerEm?: number }).unitsPerEm;
  if (!upm || upm <= 0) return null;

  let top = -Infinity;
  let bottom = Infinity;
  try {
    for (const glyph of font.glyphsForString(text)) {
      const bbox = (glyph as unknown as { bbox?: { minY: number; maxY: number } }).bbox;
      if (!bbox) continue;
      // Glyph rong (dau cach) co bbox suy bien; bo qua de khong keo pham vi ve 0.
      if (!Number.isFinite(bbox.maxY) || !Number.isFinite(bbox.minY)) continue;
      if (bbox.maxY === 0 && bbox.minY === 0) continue;
      if (bbox.maxY > top) top = bbox.maxY;
      if (bbox.minY < bottom) bottom = bbox.minY;
    }
  } catch {
    return null;
  }
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null;

  return {
    topEm: Math.max(top / upm, MIN_INK_BAND.topEm),
    bottomEm: Math.min(bottom / upm, MIN_INK_BAND.bottomEm),
  };
}

export interface CoverInput {
  /** Hop bao do tang trich xuat bao cao. */
  bbox: PdfRect;
  /** Duong co so cua dong DAU trong khoi. */
  firstBaselineY: number;
  /** Duong co so cua dong CUOI. Bang firstBaselineY khi khoi chi mot dong. */
  lastBaselineY: number;
  fontSizePt: number;
  /** Chuoi chu CU - thu quyet dinh can che cao bao nhieu. */
  originalText: string;
  /** Font da phan giai, dung lam thuoc do. Dung font thay the cung chap nhan
   *  duoc: chieu cao dau thanh khong chenh nhau nhieu giua cac font Latin. */
  font: Font | null;
  /** Dem ngang, diem. */
  padX?: number;
}

/**
 * Tinh hop che. Luon BAO TRUM bbox da bao cao - khong bao gio thu nho lai.
 */
export function computeCoverRect(input: CoverInput): PdfRect {
  const { bbox, firstBaselineY, lastBaselineY, fontSizePt, originalText, font } = input;
  const padX = input.padX ?? 0.5;

  const band = measureInkBand(font, originalText) ?? FALLBACK_INK_BAND;

  const bboxTop = bbox.y + bbox.height;
  const bboxBottom = bbox.y;

  // Pham vi muc quy ve toa do trang.
  const inkTop = firstBaselineY + band.topEm * fontSizePt;
  const inkBottom = lastBaselineY + band.bottomEm * fontSizePt;

  // Hop nhat, roi chan lai de khong an sang hang ben canh.
  const maxGrowth = MAX_GROWTH_EM * fontSizePt;
  const top = Math.min(Math.max(bboxTop, inkTop), bboxTop + maxGrowth);
  const bottom = Math.max(Math.min(bboxBottom, inkBottom), bboxBottom - maxGrowth);

  return {
    x: bbox.x - padX,
    y: bottom,
    width: bbox.width + padX * 2,
    height: top - bottom,
  };
}
